import { FC, SyntheticEvent, useEffect, useState } from 'react';
import { useAppDispatch } from '../../../hooks';
import { updateMeal, updateVariant } from '../../../store/slices';
import { ICreateMeal, ICreateVariant } from '../../../types';
import { mealsApi } from '../../../api';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { LogoutLoader } from './loaders';

interface IToggle {
  active: boolean;
  module?: string;
  primaryKey: string | number;
  item?: any;
  onToggle?: (value: boolean) => void;
}

export const Toggle: FC<IToggle> = ({
  active,
  module,
  primaryKey,
  onToggle,
}) => {
  const queryClient = useQueryClient();

  const dispatch = useAppDispatch();
  const [on, setOn] = useState(active);
  const [loading, setLoading] = useState(false);

  const handleToggle = async (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (onToggle) {
      onToggle(!on);
      return;
    }

    if (module === 'meals') {
      dispatch(
        updateMeal({
          payload: {
            status: active ? 'OUT_OF_STOCK' : 'ACTIVE',
          } as ICreateMeal,
          id: primaryKey as number,
        })
      );
    }

    if (module === 'variants') {
      dispatch(
        updateVariant({
          payload: {
            status: active ? 'OUT_OF_STOCK' : 'ACTIVE',
          } as ICreateVariant,
          id: primaryKey as number,
        })
      );
    }

    if (module === 'options') {
      setLoading(true);
      try {
        await mealsApi.updateOption(primaryKey.toString(), { active: !on });
        if (queryClient) {
          await queryClient.refetchQueries(['options']);
        }

        toast.success('Option updated!');
        setOn((prev) => !prev);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    }

    if (module === 'foodItems') {
      setLoading(true);
      try {
        await mealsApi.updateFoodItem(primaryKey.toString(), {
          status: on ? 'OUT_OF_STOCK' : 'OPEN',
        });
        if (queryClient) {
          await queryClient.refetchQueries(['food-items']);
        }
        toast.success('Food item updated!');
        setOn((prev) => !prev);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    }
  };

  // useEffect(() => {
  //   setOn(active);
  // }, [active]);

  return (
    <button
      className={`h-8 w-16 px-1 flex rounded-full items-center transition duration-300 relative ${
        on ? 'bg-primary-700' : 'bg-neutral-400'
      }`}
      onClick={handleToggle}
      disabled={loading}
      aria-busy={loading}
      aria-disabled={loading}
    >
      {loading ? (
        <div className="flex justify-center items-center w-full">
          <LogoutLoader heightWidth="w-5 h-5" />
        </div>
      ) : (
        <>
          {!on && (
            <p className="absolute top-1.5 right-2 text-sm semibold">NO</p>
          )}
          <div
            className={`h-5 w-5 rounded-full bg-white transition duration-300 ${
              on ? 'translate-x-9' : 'translate-x-0'
            }`}
          />
          {on && (
            <p className="absolute top-1.5 left-2 text-sm semibold text-white">
              YES
            </p>
          )}
        </>
      )}
    </button>
  );
};

export const CheckboxToggle: FC<IToggle> = ({ active, primaryKey, module }) => {
  const dispatch = useAppDispatch();
  const [on, setOn] = useState(active);

  const handleToggle = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (module === 'variants') {
      dispatch(
        updateVariant({
          payload: {
            status: active ? 'INACTIVE' : 'ACTIVE',
          } as ICreateVariant,
          id: primaryKey as number,
        })
      );
    } else {
      dispatch(
        updateMeal({
          payload: {
            status: active ? 'INACTIVE' : 'ACTIVE',
          } as ICreateMeal,
          id: primaryKey as number,
        })
      );
    }

    setOn((prev) => !prev);
  };

  useEffect(() => {
    setOn(active);
  }, [active]);

  return (
    <div>
      <input
        type="checkbox"
        onChange={handleToggle}
        checked={!on}
        className="h-5 w-5"
      />
    </div>
  );
};
